import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'

// Legacy join-operator tokens (v1 `searchCondition` values). Used to recognise
// the corrupted-Record state where these tokens were mistakenly stored as
// keyword values.
const LEGACY_JOIN_OPERATORS = new Set(['or', 'and', 'not', 'semantic'])

/**
 * Translate a legacy v1-style search — a join-operator array (["or"] / ["and"]
 * / ["SEMANTIC"], optionally with "not") plus the keywords that lived in
 * `searchTerms` — into the new panel search fields.
 *
 * "or" (the v1 default) → "Includes some" (include_any); "and" → "Includes all"
 * (include_all); "not" wraps both into the exclude operators (NOT(OR) →
 * "Excludes some", NOT(AND) → "Excludes all"). Semantic search keeps its phrase
 * in `searchTerms` and flips `smartSearchEnabled` instead of using a criteria
 * card.
 */
const migrateLegacySearch = (
  oldOperators: string[],
  keywords: string[],
): Pick<
  ChartPanel,
  'searchCriteria' | 'searchTerms' | 'smartSearchEnabled'
> => {
  const lowered = oldOperators.map((op) => op.toLowerCase())

  if (lowered.includes('semantic')) {
    return {
      searchCriteria: {},
      searchTerms: keywords,
      smartSearchEnabled: true,
    }
  }

  const isNot = lowered.includes('not')
  const isAnd = lowered.includes('and')
  const operatorLabel = isNot
    ? isAnd
      ? 'Excludes all'
      : 'Excludes some'
    : isAnd
      ? 'Includes all'
      : 'Includes some'

  return {
    searchCriteria: keywords.length > 0 ? { [operatorLabel]: keywords } : {},
    searchTerms: [],
    smartSearchEnabled: false,
  }
}

/**
 * Heal a panel whose search fields are in a legacy or corrupted shape, returning
 * a panel whose `searchTerms` / `searchCriteria` / `smartSearchEnabled` are in
 * the canonical v2 shape. A no-op for already-correct panels.
 *
 * Two broken states exist on disk (both predate the keyword-card format):
 *   1. Truly-old panels — `searchCriteria` is still a v1 join-operator array
 *      (e.g. `["or"]`) with the keywords in `searchTerms`.
 *   2. Panels mangled by the first store migration, which wrapped the operator
 *      array as keyword VALUES (e.g. `{ 'Includes all': ['or'] }`) and was then
 *      re-persisted, so `searchCriteria` is a Record of operator tokens while the
 *      real keywords are still stranded in `searchTerms`. That made the backend
 *      search for the literal word "or" and match nearly every post.
 *
 * Applied at the request boundary (`useChartQuery`) so EVERY chart request —
 * charts-v2, dashboards-v2 embeddables, ask-yogi — is healed regardless of how
 * the panel was loaded, and in the charts-v2 store's `hydrate` so persisted
 * charts-v2 state is repaired on load.
 */
export function normalizeLegacyPanelSearch(panel: ChartPanel): ChartPanel {
  const keywords = Array.isArray(panel.searchTerms) ? panel.searchTerms : []
  const criteria = panel.searchCriteria

  // (1) searchCriteria is still the legacy operator array.
  if (Array.isArray(criteria)) {
    return { ...panel, ...migrateLegacySearch(criteria, keywords) }
  }

  // No criteria Record to inspect (undefined/null on some saves) — nothing to
  // normalize. Guard before Object.values so a missing field can't throw and
  // break the chart query for EVERY panel.
  if (!criteria || typeof criteria !== 'object') {
    return panel
  }

  // (2) Corrupted Record: keywords stranded in `searchTerms` AND a non-empty
  // `searchCriteria` Record whose values are ONLY legacy join-operator tokens.
  // Real new-format panels carry keyword values here (and clear `searchTerms`
  // when logical cards are used), so they never match this.
  const criteriaValues = Object.values(criteria).flat()
  const looksCorrupted =
    keywords.length > 0 &&
    criteriaValues.length > 0 &&
    criteriaValues.every((value) =>
      LEGACY_JOIN_OPERATORS.has(String(value).toLowerCase()),
    )
  if (looksCorrupted) {
    return { ...panel, ...migrateLegacySearch(criteriaValues, keywords) }
  }

  return panel
}
